<template>
  <el-menu
    class="menubar"
    :collapse="sidebarIsCollapsed"
    :router="true"
    :default-active="this.$route.meta.menuActiveRouteName || this.$route.name"
  >
    <el-menu-item-group
      v-for="(group, index) in menuItemsGroups"
      :key="index"
      :title="$t(`menuItems.${group.name}`)"
    >
      <el-menu-item
        v-for="(menuItem, index) in group.children"
        :key="index"
        :index="menuItem.name"
        :route="{ name: menuItem.name, query: getTabsQuery(menuItem.name) }"
        class="menu-item"
        @click.native="onMenuItemClick(menuItem.name)"
      >
        <!-- <i :class="`menu-item__icon ${menuItem.icon}`" /> -->
        <i
          v-if="
            menuItem.name === 'approvalCustomerChangesIndex' &&
            hasPendingChanges
          "
          class="menu-item__icon el-icon-warning"
        />
        <span
          v-if="menuItem.name === 'stabilityConfigurationsIndex'"
          slot="title"
        >
          <el-badge
            :value="pendingStabilityCount"
            :hidden="pendingStabilityCount === 0"
            type="primary"
            class="stability-badge"
          >
            <span>{{ $t(`menuItems.${menuItem.name}`) }}</span>
          </el-badge>
        </span>
        <span
          v-else
          slot="title"
          >{{ $t(`menuItems.${menuItem.name}`) }}</span
        >
      </el-menu-item>
    </el-menu-item-group>
  </el-menu>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { samplesApi, stabilityApi } from '@/api';
  import acl from '../services/acl';

  export default {
    name: 'SidebarMenu',
    components: {},
    props: {
      menuItems: {
        type: Array,
        default: () => [],
      },
      sidebarIsCollapsed: {
        type: Boolean,
        default: () => false,
      },
    },
    data() {
      return {
        menuItemsPermitted: [],
        hasPendingChanges: false,
        pendingStabilityCount: 0,
      };
    },
    computed: {
      ...mapGetters(['tabs']),
      menuItemsGroups() {
        return this.menuItemsPermitted.filter(
          (i) => i.children && i.children.length > 0
        );
      },
      shouldCheckCustomerChanges() {
        return this.menuItemsPermitted.some((group) =>
          group.children?.some(
            (child) => child.name === 'approvalCustomerChangesIndex'
          )
        );
      },
      shouldCheckStability() {
        return this.menuItemsPermitted.some((group) =>
          group.children?.some(
            (child) => child.name === 'stabilityConfigurationsIndex'
          )
        );
      },
    },
    methods: {
      getTabsQuery(routeName) {
        const tab = this.tabs.find((t) => t.name === routeName);
        return tab && tab.query ? tab.query : {};
      },
      async checkChanges() {
        try {
          const res = await samplesApi.checkChanges();
          this.hasPendingChanges = res.data.count > 0;
        } catch (e) {
          this.hasPendingChanges = false;
        }
      },
      async checkPendingStability() {
        try {
          const res = await stabilityApi.count({ status: 'pending' });
          this.pendingStabilityCount = res.data.count;
        } catch (e) {
          this.pendingStabilityCount = 0;
        }
      },
      onMenuItemClick() {
        this.checkChanges();
        if (this.shouldCheckStability) this.checkPendingStability();
      },
    },
    beforeDestroy() {
      this.$root.$off(
        'stability:refresh-pending-count',
        this.checkPendingStability
      );
    },
    created() {
      // filter menuItems by permissions
      // this.menuItemsPermitted = this.menuItems.filter(
      //   (menuItem) => acl.userCanUse(
      //     this.$router.resolve({ name: menuItem.name }).resolved.meta.routePermissions,
      //   ),
      // );
      this.menuItemsPermitted = this.menuItems.reduce((acc, parentItem) => {
        if (parentItem.children) {
          const permittedChildren = {
            ...parentItem,
            children: parentItem.children.filter((child) =>
              acl.userCanUse(
                this.$router.resolve({ name: child.name }).resolved.meta
                  .routePermissions
              )
            ),
          };
          return acc.concat(permittedChildren);
        }
        const parentPermitted = acl.userCanUse(
          this.$router.resolve({ name: parentItem.name }).resolved.meta
            .routePermissions
        );
        if (parentPermitted) return acc.concat(parentItem);
        return acc;
      }, []);

      if (this.shouldCheckCustomerChanges) {
        this.checkChanges();
      }
      if (this.shouldCheckStability) {
        this.checkPendingStability();
        this.$root.$on(
          'stability:refresh-pending-count',
          this.checkPendingStability
        );
      }
    },
  };
</script>

<style lang="scss">
  .menubar {
    &.el-menu {
      border-right: 0;
    }

    &:not(.el-menu--collapse) {
      width: 280px;
    }

    .menu-item {
      padding: 0 0px 0 20px;
      color: $--color-white;
      border-bottom: 1px solid $--color-black-lighter;
      font-size: 1rem;
      height: 2rem;
      line-height: 1.5rem;

      &.is-active {
        background-color: $--background-color-lighter;
        color: $--color-success;
      }
      &:hover,
      &:focus {
        background-color: $--background-color-lighter;
        color: $--color-success;

        .menu-item__icon {
          color: $--color-primary;
        }
      }

      &__icon {
        color: $--color-white;
        margin-right: 3px;
        width: 14px;
        text-align: center;
        font-size: 14px;
        vertical-align: text-bottom;
        transition: border-color 0.3s, background-color 0.3s, color 0.3s;
      }
    }

    .stability-badge {
      padding-right: 1.2rem;
    }

    .el-menu-item-group:first-child {
      .el-menu-item-group__title {
        padding-top: 0;
      }
    }

    .el-menu-item-group__title {
      padding: 1.25rem 0 0.75rem 1.25rem;
      font-size: 1.4rem;
      color: $--color-white;
      font-weight: 700;
    }
  }
</style>
